import { format, isToday } from 'date-fns'
import { useAppMutation } from '@/helpers/api/mutation'

import { createInstance } from '@/helpers/api/base'
import { useNotification } from '@/ui/Notification'
import { Log } from '@/types/logs'
import { DeleteModalProps, ModalTypes, useModal } from '@/components/Modal'
import { useGetControllerLogParams } from '@/modules/CombinedLogs/apiHelpers/useGetControllerLogParams'

interface Props {
    log?: Log
    callback?: () => void
}

export const useDeleteMechanicalInformation = ({ log, callback }: Props) => {
    const { showNotification } = useNotification()
    const { openModal } = useModal()
    const logParams = useGetControllerLogParams()

    const deleteMechanicalInformation = useAppMutation<string, Error, string, Log[]>({
        mutationFn: (reportId: string) =>
            createInstance({
                url: `/mechanical-info/${reportId}`,
                method: 'DELETE',
            }),
        queryKey: ['controllerLogs', ...logParams],
        optimisticChangesCallback: (requestBody: string, previousLogs?: Log[]) =>
            previousLogs
                ? previousLogs.map((item) =>
                      item.mechanicalInfo && item.mechanicalInfo.id === requestBody
                          ? { ...item, mechanicalInfo: null }
                          : item
                  )
                : [],
        onSuccess: () => {
            showNotification({ message: 'Mechanical information was deleted' })
        },
        alertOnError: true,
    })

    const handleDeleteMechanicalInformation = () => {
        if (!log) {
            return
        }

        const deleteFrom = isToday(log.timestamp) ? 'today' : format(log.timestamp, 'd/MM/y')

        openModal<DeleteModalProps>(ModalTypes.deleteModal, {
            title: 'Delete mechanical information?',
            message: (
                <>
                    This will delete this <strong>Mechanical information</strong> from <strong>{deleteFrom}</strong>.
                    This cannot be undone.
                </>
            ),
            onDelete: () => {
                if (!log.mechanicalInfo) {
                    return
                }

                if (callback) {
                    callback()
                } else {
                    deleteMechanicalInformation.mutate(log.mechanicalInfo.id)
                }
            },
        })
    }

    return {
        handleDeleteMechanicalInformation,
    }
}
